import React, { useState, useRef, useEffect, useCallback } from 'react';

function SelectEditor({ value, onChange, disabled, options, loading, onAutoSave }) {
  const [search, setSearch] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const searchRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (searchRef.current) {
      searchRef.current.focus();
    }
  }, []);

  const filtered = (options || []).filter(opt => {
    const label = opt.name || opt.value || '';
    return label.toLowerCase().includes(search.toLowerCase());
  });

  // Total navigable items: None (index 0) + filtered options (index 1..N)
  const totalItems = filtered.length + 1;

  // Reset highlight when search changes
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [search]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex < 0 || !listRef.current) return;
    const items = listRef.current.querySelectorAll('.field-editor-option');
    if (items[highlightedIndex]) {
      items[highlightedIndex].scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex]);

  const handleSelect = useCallback((opt) => {
    if (disabled) return;
    if (onAutoSave) onAutoSave(opt);
    else onChange(opt);
  }, [disabled, onAutoSave, onChange]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev => (prev + 1) % totalItems);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => (prev <= 0 ? totalItems - 1 : prev - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedIndex === 0) {
        handleSelect(null);
      } else if (highlightedIndex > 0 && highlightedIndex <= filtered.length) {
        handleSelect(filtered[highlightedIndex - 1]);
      }
    }
  }, [totalItems, highlightedIndex, filtered, handleSelect]);

  const currentId = value?.id || value;

  return (
    <div className="field-editor-select-wrapper">
      <input
        ref={searchRef}
        type="text"
        className="field-editor-search-input"
        placeholder="Search..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
      />
      {loading ? (
        <div className="field-editor-loading">Loading options...</div>
      ) : (
        <div className="field-editor-options-list" ref={listRef}>
          {/* None option for nullable fields */}
          <div
            className={`field-editor-option ${!currentId ? 'selected' : ''} ${highlightedIndex === 0 ? 'highlighted' : ''}`}
            onClick={() => handleSelect(null)}
            onMouseEnter={() => setHighlightedIndex(0)}
          >
            <span className="field-editor-option-label">None</span>
          </div>
          {filtered.map((opt, idx) => {
            const optId = opt.id || opt.value;
            const isSelected = String(currentId) === String(optId);
            const itemIndex = idx + 1;
            return (
              <div
                key={optId}
                className={`field-editor-option ${isSelected ? 'selected' : ''} ${highlightedIndex === itemIndex ? 'highlighted' : ''}`}
                onClick={() => handleSelect(opt)}
                onMouseEnter={() => setHighlightedIndex(itemIndex)}
              >
                {opt.iconUrl && (
                  <img src={opt.iconUrl} alt="" className="field-editor-option-icon" />
                )}
                <span className="field-editor-option-label">{opt.name || opt.value}</span>
                {isSelected && <span className="field-editor-option-check">✓</span>}
              </div>
            );
          })}
          {filtered.length === 0 && !loading && (
            <div className="field-editor-empty">No options found</div>
          )}
        </div>
      )}
    </div>
  );
}

export default SelectEditor;
